import styled from '@emotion/styled';
import { down, up } from '../../../../style-utils/media-queries';
import { layout, palette, radius, shadow, spacing, status, typography } from '../../../../style-utils/theme';

/**
 * The Alerts tab's parts (plan S8, SC-1). Tokens only; phone first — the cards
 * stack, chip groups wrap, and from lg the settings sit left of the studio and
 * the history.
 */

export const Page = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

export const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.md};
  align-items: start;

  ${up('lg')} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};

  ${up('md')} {
    padding: ${spacing.md};
  }
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const GroupLabel = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.inkSoft};
`;

export const Note = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

/** The send time under a history row's status: one line ("26 Oct 2026, 19:25"), or the table squeezes it to a
 * 53px column three lines tall at 1280. */
export const SentAt = styled(Note)`
  white-space: nowrap;
`;

export const Check = styled.label`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing.sm};
  align-items: center;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  cursor: pointer;

  input {
    width: ${spacing.md};
    height: ${spacing.md};
    margin: 0;
    accent-color: ${palette.teal};
  }
`;

export const SlotRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  align-items: center;

  input {
    flex: 1 1 140px;
    max-width: 200px;
  }
`;

export const Preview = styled.div`
  min-height: ${spacing.huge};
  padding: ${spacing.sm};
  background: ${palette.surfaceMuted};
  border-radius: ${radius.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const PreviewTitle = styled.p`
  margin: 0 0 ${spacing.xs};
  font-weight: ${typography.weight.semibold};
`;

export const TruncNote = styled.p<{ $warn: boolean }>`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${p => (p.$warn ? status.warning.fg : palette.slate)};
`;

/** $float: there is something to save (or a save / reload in hand). */
export const Footer = styled.div<{ $float: boolean }>`
  position: sticky;
  bottom: calc(var(--safe-bottom, 0px) + ${spacing.xs});
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
  align-items: center;
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.md};

  /* Below md the menu is a fixed bottom bar: stick ABOVE it (same reserve as layout-container.styled.tsx and
     iq-dock.styled.tsx). And only while there is something to save: a sticky box cannot leave its form, and on
     a phone the listing header puts the form's top near the screen's bottom, so a bar pinned from the start
     would sit half under the menu until the host scrolls. To change anything they scroll into the form first. */
  ${down('md')} {
    position: ${p => (p.$float ? 'sticky' : 'static')};
    bottom: calc(${layout.navHeight} + var(--safe-bottom, 0px) + ${spacing.xs});
  }

  ${up('lg')} {
    position: static;
    box-shadow: ${shadow.sm};
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};

  th {
    padding: ${spacing.xs};
    text-align: left;
    font-size: ${typography.size.xxs}px;
    font-weight: ${typography.weight.semibold};
    letter-spacing: ${typography.tracking.caps};
    text-transform: uppercase;
    color: ${palette.slate};
    border-bottom: 1px solid ${palette.line};
  }

  td {
    padding: ${spacing.xs};
    border-bottom: 1px solid ${palette.line};
    vertical-align: middle;
  }

  ${down('md')} {
    thead {
      display: none;
    }
    tr {
      display: block;
      padding: ${spacing.xs} 0;
      border-bottom: 1px solid ${palette.line};
    }
    td {
      display: grid;
      grid-template-columns: 112px 1fr;
      gap: ${spacing.xs};
      border-bottom: 0;
      padding: ${spacing.half} 0;
    }
    td::before {
      content: attr(data-label);
      color: ${palette.slate};
      font-size: ${typography.size.xs}px;
    }
  }
`;
